import { saveFullUser } from './saveUserAction'

export type ImportUserRow = {
  givenName: string
  familyName: string
  middleName: string
  nickName: string
  israeliId: string
  dateOfBirth: string
  gender: string
  maritalStatus: string
  mainMobilePhone: string
  email: string
  addressCity: string
  addressStreet: string
  addressHouseNumber: string
  shelter: string
  contactPerson1: string
  contactPersonCellPhone1: string
  contactPerson2: string
  contactPersonCellPhone2: string
  groups: string
  spouseId: string
  spouseIdentityNumber: string
  spouseName: string
  userType: string
  roleInCommunity: string
}

const rowToForm = (row: ImportUserRow) => ({
  id: 0,
  population: {
    firstName: row.givenName,
    lastName: row.familyName,
    middleName: row.middleName,
    nickName: row.nickName,
    identityNumber: row.israeliId,
    birthDate: (() => {
      if (!row.dateOfBirth) return null
      const [day, month, year] = row.dateOfBirth.split('/')
      if (!day || !month || !year) return null
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00Z`
    })(),
    gender: row.gender,
    maritalStatus: row.maritalStatus,
    phone: row.mainMobilePhone,
    email: row.email,
    city: row.addressCity,
    street: row.addressStreet,
    houseNumber: row.addressHouseNumber,
    shelter: row.shelter,
    roleInCommunity: row.roleInCommunity || '',
    userTypes: row.userType
      ? row.userType.split(',').map(t => t.trim()).filter(Boolean).map(tagName => ({ tagName, tagTypeId: 1, action: 'fromList' }))
      : [],
    isDead: false,
    isLocalMember: true,
    deathDate: null,
  },
  emergency: {
    contacts: [
      ...(row.contactPerson1 ? [{ mode: 'manual', name: row.contactPerson1, phone: row.contactPersonCellPhone1, address: '' }] : []),
      ...(row.contactPerson2 ? [{ mode: 'manual', name: row.contactPerson2, phone: row.contactPersonCellPhone2, address: '' }] : []),
    ],
  },
  family: {
    household: null,
    spouse: (() => {
      if (!row.spouseName && !row.spouseIdentityNumber) return null
      const [firstName = '', ...rest] = (row.spouseName || '').split(' ')
      return {
        id: parseInt(row.spouseId) || 0,
        firstName,
        lastName: rest.join(' '),
        identityNumber: row.spouseIdentityNumber || '',
        phone: '',
      }
    })(),
    children: [],
  },
  contacts: {
    roles: [],
    tags: [],
    groups: row.groups ? row.groups.split(',').map(g => g.trim()).filter(Boolean) : [],
    permissions: {},
  },
})

// Fake path — bulk import endpoint not ready yet
// Uses saveFullUser (same as single user creation) for each row
export type RowStatus = 'success' | 'error'

const MISSING_CONTACT_CODES: Record<string, string> = {
  MissingMekomeContact: 'שדות חובה חסרים ליצירת איש קשר',
}

const parseImportError = (err: any): string => {
  if (err?.status === 409 && err?.data?.code === 'DUPLICATE_USER') {
    const u = err.data.existingUser
    const parts = ['משתמש כבר קיים במערכת']
    if (u?.existingIsraeliId) parts.push(`ת.ז.: ${u.existingIsraeliId}`)
    if (u?.existingMainMobile) parts.push(`נייד: ${u.existingMainMobile}`)
    return parts.join(' | ')
  }
  if (err?.status === 422) {
    const code = err?.data?.code
    return MISSING_CONTACT_CODES[code] ?? `שגיאת ולידציה: ${code || 'לא ידוע'}`
  }
  return 'שגיאה'
}

export const importUsers = async (
  users: ImportUserRow[],
  communityId: number,
  onProgress?: (index: number, total: number, status: RowStatus, message?: string) => void
): Promise<{ success: boolean; imported: number }> => {
  let imported = 0
  for (let i = 0; i < users.length; i++) {
    try {
      await saveFullUser(rowToForm(users[i]), communityId)
      imported++
      onProgress?.(i, users.length, 'success')
    } catch (err) {
      onProgress?.(i, users.length, 'error', parseImportError(err))
    }
  }
  return { success: true, imported }
}
